import React, { useState } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { db } from "../service/firebase";
import bcrypt from "bcryptjs";
import "./JResDeleteAccountPlayStore.css";

const JResDeleteAccountPlayStore = () => {
  const [step, setStep] = useState(1); // 1: login, 2: verification, 3: confirmation
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  // Step 1: Login with mobile and password
  const handleLogin = async () => {
    if (!mobile || mobile.length !== 10 || isNaN(mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Find user by mobile number in jres collection
      const q = query(collection(db, "jres"), where("mobile", "==", mobile));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("No JRes account found with this mobile number");
        setLoading(false);
        return;
      }

      // Get the first matching user (should be only one)
      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();

      // Verify password
      const isPasswordValid = await bcrypt.compare(password, userData.passwordHash);
      if (!isPasswordValid) {
        setError("Invalid password. Please try again.");
        setLoading(false);
        return;
      }

      // Store user info and proceed to verification step
      setUserInfo({
        userId: userDoc.id,
        mobile: userData.mobile,
        name: userData.name || 'Not specified',
        email: userData.email || 'Not specified',
        createdAt: userData.createdAt
      });
      setStep(2);
      setError("");
    } catch (err) {
      console.error("Login error:", err);
      setError("Failed to verify account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Final confirmation and deletion
  const deleteAccountPermanently = async () => {
    if (confirmText !== "DELETE MY ACCOUNT") {
      setError('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (!userInfo) {
      setError("Account information not found. Please start over.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const batch = writeBatch(db);
      const { userId, mobile } = userInfo;

      // Verify account still exists before deletion
      const userRef = doc(db, "jres", userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        setError("Account not found. It may have been already deleted.");
        setLoading(false);
        return;
      }

      // 1. Delete user's main document from jres collection
      batch.delete(userRef);

      // 2. Delete user's orders if exists
      try {
        const ordersQuery = query(
          collection(db, "orders"),
          where("userId", "==", userId)
        );
        const ordersSnapshot = await getDocs(ordersQuery);
        ordersSnapshot.forEach((orderDoc) => {
          batch.delete(orderDoc.ref);
        });
      } catch (err) {
        console.log("No orders to delete");
      }

      // 3. Delete user's profile data if exists
      try {
        const profileQuery = query(
          collection(db, "profiles"),
          where("userId", "==", userId)
        );
        const profileSnapshot = await getDocs(profileQuery);
        profileSnapshot.forEach((profileDoc) => {
          batch.delete(profileDoc.ref);
        });
      } catch (err) {
        console.log("No profile data to delete");
      }

      // 4. Delete user's addresses if exists
      try {
        const addressesQuery = query(
          collection(db, "addresses"),
          where("userId", "==", userId)
        );
        const addressesSnapshot = await getDocs(addressesQuery);
        addressesSnapshot.forEach((addressDoc) => {
          batch.delete(addressDoc.ref);
        });
      } catch (err) {
        console.log("No addresses to delete");
      }

      // 5. Delete user's cart items if exists
      try {
        const cartQuery = query(
          collection(db, "cart"),
          where("userId", "==", userId)
        );
        const cartSnapshot = await getDocs(cartQuery);
        cartSnapshot.forEach((cartDoc) => {
          batch.delete(cartDoc.ref);
        });
      } catch (err) {
        console.log("No cart items to delete");
      }

      // 6. Delete user's favorites if exists
      try {
        const favoritesQuery = query(
          collection(db, "favorites"),
          where("userId", "==", userId)
        );
        const favoritesSnapshot = await getDocs(favoritesQuery);
        favoritesSnapshot.forEach((favDoc) => {
          batch.delete(favDoc.ref);
        });
      } catch (err) {
        console.log("No favorites to delete");
      }

      // 7. Delete any OTPs associated with this mobile
      const otpQuery = query(
        collection(db, "otps"),
        where("mobile", "==", `+91${mobile}`)
      );
      const otpSnapshot = await getDocs(otpQuery);
      otpSnapshot.forEach((otpDoc) => {
        batch.delete(otpDoc.ref);
      });

      // 8. Delete user's notifications if any
      try {
        const notificationsQuery = query(
          collection(db, "notifications"),
          where("userId", "==", userId)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        notificationsSnapshot.forEach((notifDoc) => {
          batch.delete(notifDoc.ref);
        });
      } catch (err) {
        console.log("No notifications to delete");
      }

      // 9. Delete user's activity logs if any
      try {
        const activityQuery = query(
          collection(db, "activity"),
          where("userId", "==", userId)
        );
        const activitySnapshot = await getDocs(activityQuery);
        activitySnapshot.forEach((activityDoc) => {
          batch.delete(activityDoc.ref);
        });
      } catch (err) {
        console.log("No activity logs to delete");
      }

      // Execute all deletions
      await batch.commit();

      // Show success and reset
      setStep(3);
      setError("");
      
    } catch (err) {
      console.error("Account deletion error:", err);
      setError("Failed to delete account. Please try again or contact support.");
    } finally {
      setLoading(false);
    }
  };

  // Reset form to start over
  const resetForm = () => {
    setStep(1);
    setMobile("");
    setPassword("");
    setConfirmText("");
    setUserInfo(null);
    setError("");
  };

  return (
    <div className="jres-delete-wrapper">
      <div className="jres-delete-container">
        <header className="jres-app-header">
          <h1 className="jres-app-name">JRE</h1>
          <p className="jres-app-tagline">Account Deletion Request</p>
        </header>

        {/* Data Deletion Information */}
        <section className="jres-data-deletion-info">
          <h2>📱 JRE Account Deletion Process</h2>
          <p>
            To request deletion of your JRE account and associated data, 
            please follow the steps below. This process is permanent and irreversible.
          </p>

          <div className="jres-data-types">
            <div className="jres-data-section">
              <h3>✅ Data That Will Be Deleted Immediately</h3>
              <ul>
                <li>Your personal profile information (name, email, mobile number)</li>
                <li>Account credentials and login information</li>
                <li>All your order history and transaction records</li>
                <li>Saved shipping and billing addresses</li>
                <li>Shopping cart items and saved preferences</li>
                <li>Wishlist and favorite items</li>
                <li>Notification preferences and settings</li>
                <li>OTP verification records</li>
                <li>Activity logs and usage history</li>
                <li>All other JRes account-associated data</li>
              </ul>
            </div>

            <div className="jres-data-section">
              <h3>⏳ Data Retention & Legal Compliance</h3>
              <ul>
                <li><strong>Immediate Deletion:</strong> Most personal data is deleted immediately upon account deletion request</li>
                <li><strong>30-Day Backup Period:</strong> Some data may remain in secure backups for up to 30 days for security purposes</li>
                <li><strong>Legal Requirements:</strong> Financial transaction records may be retained as required by applicable laws and regulations</li>
                <li><strong>Fraud Prevention:</strong> Minimal data may be retained to prevent fraud and maintain platform security</li>
                <li><strong>Service Recovery:</strong> Some data may be retained for 30 days to allow account recovery in case of accidental deletion</li>
              </ul>
            </div>

            <div className="jres-data-section">
              <h3>📋 Steps to Delete Your Account</h3>
              <ol>
                <li>Enter your registered mobile number and password</li>
                <li>Verify your account ownership</li>
                <li>Confirm deletion by typing "DELETE MY ACCOUNT"</li>
                <li>Receive confirmation of account deletion</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Account Deletion Form */}
        <section className="jres-deletion-form-section">
          <h2>🔐 Request JRE Account Deletion</h2>
          
          {step === 1 && (
            <div className="jres-login-step">
              <h3>Step 1: Verify Your JRE Account</h3>
              <p>Enter your registered mobile number and password to continue with account deletion.</p>
              
              <div className="jres-form-group">
                <label htmlFor="jresMobile">Registered Mobile Number</label>
                <input
                  id="jresMobile"
                  type="tel"
                  placeholder="Enter your 10-digit mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
                  maxLength="10"
                  disabled={loading}
                />
              </div>

              <div className="jres-form-group">
                <label htmlFor="jresPassword">Account Password</label>
                <input
                  id="jresPassword"
                  type="password"
                  placeholder="Enter your JRes account password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') handleLogin();
                  }}
                />
              </div>

              {error && <div className="jres-error-message">{error}</div>}

              <button
                className="jres-verify-btn"
                onClick={handleLogin}
                disabled={loading || !mobile || mobile.length !== 10 || !password}
              >
                {loading ? "Verifying JRes Account..." : "Verify Account"}
              </button>
            </div>
          )}

          {step === 2 && userInfo && (
            <div className="jres-confirmation-step">
              <h3>Step 2: Final Confirmation</h3>
              <div className="jres-account-summary">
                <p><strong>JRes Account:</strong> +91 {userInfo.mobile}</p>
                <p><strong>User ID:</strong> {userInfo.userId}</p>
                <p><strong>Name:</strong> {userInfo.name}</p>
                <p><strong>Email:</strong> {userInfo.email}</p>
                <p><strong>Account Created:</strong> {userInfo.createdAt?.toDate?.().toLocaleDateString() || 'Unknown'}</p>
              </div>
              
              <div className="jres-final-warning">
                <h4>⚠️ Final Warning - Permanent Action</h4>
                <p>
                  You are about to <strong>permanently delete</strong> your JRes account. 
                  This action <strong>cannot be undone or reversed</strong>. All your data including 
                  profile information, order history, and preferences will be permanently erased.
                </p>
                <p style={{marginTop: '15px', fontWeight: 'bold'}}>
                  ⚠️ You will lose access to all your account features and history.
                </p>
              </div>

              <div className="jres-form-group">
                <label htmlFor="jresConfirmText">
                  To confirm account deletion, please type <strong>"DELETE MY ACCOUNT"</strong> exactly as shown:
                </label>
                <input
                  id="jresConfirmText"
                  type="text"
                  placeholder='Type "DELETE MY ACCOUNT" to confirm permanent deletion'
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  disabled={loading}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && confirmText === "DELETE MY ACCOUNT") {
                      deleteAccountPermanently();
                    }
                  }}
                />
              </div>

              {error && <div className="jres-error-message">{error}</div>}

              <div className="jres-button-group">
                <button
                  className="jres-back-btn"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Go Back
                </button>
                <button
                  className="jres-delete-btn"
                  onClick={deleteAccountPermanently}
                  disabled={loading || confirmText !== "DELETE MY ACCOUNT"}
                >
                  {loading ? "Deleting JRes Account..." : "Permanently Delete My Account"}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="jres-success-step">
              <div className="jres-success-icon">✅</div>
              <h3>JRes Account Deletion Successful</h3>
              <p>
                Your JRes account and all associated personal data have been scheduled for deletion. 
              </p>
              <div className="jres-deletion-summary">
                <p><strong>What happens next:</strong></p>
                <ul>
                  <li>Your personal profile information has been deleted</li>
                  <li>All your order history and transaction records have been removed</li>
                  <li>Your saved addresses and preferences have been erased</li>
                  <li>Most personal data has been deleted immediately</li>
                  <li>Remaining data in backups will be purged within 30 days</li>
                  <li>You will no longer be able to access your JRes account</li>
                </ul>
              </div>
              <p>
                <strong>Thank you for using JRes. We hope to serve you again in the future.</strong>
              </p>
              <button
                className="jres-new-request-btn"
                onClick={resetForm}
              >
                Delete Another Account
              </button>
            </div>
          )}
        </section>

        {/* Support Information */}
        <section className="jres-support-section">
          <h3>Need Help with Account Deletion?</h3>
          <p>
            If you encounter any issues during the account deletion process or have questions 
            about your data, our support team is here to help:
          </p>
          {/* <div className="jres-support-contact">
            <img 
              src="/whatsapp.svg" 
              alt="WhatsApp" 
              className="jres-whatsapp-icon"
              onClick={() => window.open("https://wa.me/918917412728", "_blank")}
            />
            <span>+91 89174 12728 (JRes WhatsApp Support)</span>
          </div> */}
          <div className="jres-support-email">
            <p>Email: <a href="mailto:support@jewelmartindia.com">support@jewelmartindia.com</a></p>
          </div>
          <div className="jres-support-hours">
            <p><strong>Support Hours:</strong> Monday - Saturday, 9:00 AM - 6:00 PM IST</p>
          </div>
        </section>

        <footer className="jres-page-footer">
          <p>© {new Date().getFullYear()} JRes. All rights reserved.</p>
          <p className="jres-footer-note">
            Protecting your privacy and data is our priority.
          </p>
        </footer>
      </div>
    </div>
  );
};

export default JResDeleteAccountPlayStore;